var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { parse } from "./Parse.js";
import { isString, isFunction, isInteger } from "../TypeAssert.js";
import { hasProperty } from "../Object/HasProperty.js";
import "../Errors/index.js";
import { equal } from "../Object/Equal.js";
import { ensureArray } from "../Array/EnsureArray.js";
import { sanitizeValue } from "../Storage/SanitizeValue.js";
import { isDomain, isIPv4Address } from "../Network.js";
class Url {
  constructor(url) {
    // 原始url
    __publicField(this, "originUrl", "");
    // 监听hash存放的回调函数列表
    __publicField(this, "_hashChangeCallbackList", []);
    // 解析后的url对象值
    __publicField(this, "_urlInfo", {
      host: "",
      port: "",
      hostName: "",
      queryObj: {},
      queryRaw: "",
      hash: "",
      hashRaw: "",
      path: "",
      protocol: ""
    });
    this.originUrl = url;
    this._urlInfo = parse(url);
  }
  /**
   * 获取url解析对象属性
   */
  get urlInfo() {
    return this._urlInfo;
  }
  getValueByKey(queryKey, options) {
    const {
      shouldXssFilter = true
    } = options ?? {};
    if (isString(queryKey)) {
      if (hasProperty(this._urlInfo.queryObj, [queryKey])) {
        return shouldXssFilter ? sanitizeValue(this._urlInfo.queryObj[queryKey]) : this._urlInfo.queryObj[queryKey];
      }
      return "";
    }
    const queryObj = this._urlInfo.queryObj;
    const result = queryKey.reduce((acc, cur) => {
      if (hasProperty(queryObj, [cur])) acc[cur] = queryObj[cur];
      return acc;
    }, {});
    return shouldXssFilter ? sanitizeValue(result) : result;
  }
  /**
   * 删除url在query上面key对应的值
   * @param queryKey 需要被删掉的参数
   * @returns 删除后的url
   */
  deleteByKey(queryKey) {
    const { queryObj } = this._urlInfo;
    for (const key of ensureArray(queryKey)) {
      if (hasProperty(queryObj, [key])) delete queryObj[key];
    }
    this._updateQueryRaw();
    return this;
  }
  /**
   * 修改query参数
   * @param queryKey 需要修改的参数
   * @param queryValue 需要修改的值
   * @returns 修改后的url
   */
  modifyByKey(queryKey, queryValue) {
    const { queryObj } = this._urlInfo;
    if (hasProperty(queryObj, [queryKey])) {
      this._urlInfo.queryObj[queryKey] = queryValue;
    }
    this._updateQueryRaw();
    return this;
  }
  /**
   * 增添query
   * @param queryKey 需要添加的参数
   * @param queryValue 需要添加的值
   * @returns 增添之后的url
   */
  addValueByKey(queryKey, queryValue) {
    this._urlInfo.queryObj[queryKey] = queryValue;
    this._updateQueryRaw();
    return this;
  }
  /**
   * 与其他url相比较()
   * @param url 比较的url
   * @returns 是否相同
   */
  isSame(url) {
    const { protocol, host, path, queryObj, hashRaw } = parse(url);
    if (protocol !== this._urlInfo.protocol || host !== this._urlInfo.host || path !== this._urlInfo.path || hashRaw !== this._urlInfo.hashRaw) {
      return false;
    }
    return equal(this._urlInfo.queryObj, queryObj);
  }
  /**
   * 返回url字符串
   * @returns url字符串
   */
  stringify() {
    const { protocol, host, path, queryRaw, hashRaw } = this._urlInfo;
    return `${protocol}//${host}${path}${queryRaw}${hashRaw}`;
  }
  /**
   * 重定向
   */
  redirect(nextUrl) {
    if (window) {
      window.location.href = nextUrl;
    }
  }
  /**
   * 获取哈希值
   * @returns 哈希值
   */
  getHashValue() {
    return this._urlInfo.hash;
  }
  /**
   * 检测url上面hash值的变化
   * @param callBack 回调函数
   * @returns 无返回值回调函数id(用于删除指定回调方案)
   */
  onHashChange(callBack) {
    if (!window) return;
    this._hashChangeCallbackList.push(callBack);
    if (hasProperty(window, ["onhashchange"])) {
      window.onhashchange = () => {
        this._updateHash(window.location.hash);
        for (const callback of this._hashChangeCallbackList) {
          if (isFunction(callBack)) callback(this._urlInfo.hash);
        }
      };
    }
    return this._hashChangeCallbackList.length;
  }
  /**
   * 移除hashChange事情的某一个回调函数
   * @param id hashChange的回调函数列表编号
   * @returns 是否成功
   */
  clearOnHashChangeCallBack(id) {
    if (!window) return;
    if (!isInteger(id) || id >= this._hashChangeCallbackList.length || id <= 0) return;
    this._hashChangeCallbackList.splice(id, 1, null);
  }
  /**
   * 移除监听hash值变化事件
   * @returns 是否取消监听事件
   */
  removeOnHashChange() {
    if (!window) return false;
    if (hasProperty(window, ["onhashchange"])) {
      window.onhashchange = null;
      this._hashChangeCallbackList = [];
      return true;
    }
    return false;
  }
  /**
   * 检测url是否合法
   * @requires boolean
   */
  static isValidUrl(url) {
    try {
      const urlInfo = this.parse(url);
      if (urlInfo.hostName && urlInfo.protocol) {
        if (!/^(https?)(?=:?$)/i.test(urlInfo.protocol)) {
          return false;
        }
        if (isDomain(urlInfo.hostName) || isIPv4Address(urlInfo.hostName)) {
          return true;
        }
        return false;
      } else {
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }
  /**
   * 更新query字符串形式的值
   */
  _updateQueryRaw() {
    const { queryObj } = this._urlInfo;
    if (Object.keys(queryObj).length === 0) {
      this._urlInfo.queryRaw = "";
    } else {
      this._urlInfo.queryRaw = "?" + Object.keys(queryObj).map(function(key) {
        return key + "=" + encodeURI(queryObj[key]);
      }).join("&");
    }
  }
  /**
   * 更新hash
   * @param newHash 新的hash值
   */
  _updateHash(newHash) {
    this._urlInfo.hashRaw = newHash;
    this._urlInfo.hash = decodeURI(newHash).substring(1);
  }
}
/**
 * 解析方法
 */
__publicField(Url, "parse", parse);
export {
  Url
};
//# sourceMappingURL=index.js.map
